import React, { useState, useEffect, useRef, useCallback } from 'react';
import config, { authFetch, clearAuthToken } from './config';
import './App.css';
import Auth from './components/Auth';
import ReviewsList from './components/ReviewsList';
import PDFsList from './components/PDFsList';
import PDFViewer from './components/PDFViewer';
import CommentPanel from './components/CommentPanel';
import SharePanel from './components/SharePanel';
import UploadLanding from './components/UploadLanding';

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Navigation state: 'auth' | 'upload' | 'reviews' | 'pdfs' | 'editor'
  const [currentView, setCurrentView] = useState('auth');
  const [selectedReview, setSelectedReview] = useState(null);
  const [isSharedReview, setIsSharedReview] = useState(false);
  const [editorBackTarget, setEditorBackTarget] = useState('reviews'); // 'upload' | 'reviews' | 'pdfs'
  const [pdfCurrentReview, setPdfCurrentReview] = useState(null); // { id, title } when PDF is assigned to a review
  const [pdfAssignedReviews, setPdfAssignedReviews] = useState([]); // All reviews this PDF is assigned to
  
  // PDF editor state
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  const [shareId, setShareId] = useState(null);
  const [comments, setComments] = useState([]);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [commentPanelVisible, setCommentPanelVisible] = useState(true);
  const [pdfReady, setPdfReady] = useState(false);
  const commentDeleteRef = useRef(null);
  const commentUpdateRef = useRef(null);
  const commentReplyRef = useRef(null);
  const commentMarkReadRef = useRef(null);
  const exportRef = useRef(null);
  const [isSharedPdfView, setIsSharedPdfView] = useState(false);
  
  // User name for PDF editor (use authenticated user's name)
  const userName = user?.name || `User ${Math.floor(Math.random() * 10000)}`;

  const resetEditorState = () => {
    setPdfUrl(null);
    setPdfId(null);
    setShareId(null);
    setComments([]);
    setSelectedCommentId(null);
    setPdfReady(false);
    setPdfAssignedReviews([]);
  };

  const loadSharedPDF = useCallback(async (shareIdParam, reviewContextIdParam) => {
    try {
      console.log('[App] loadSharedPDF start:', {
        shareIdParam,
        reviewContextIdParam,
      });
      // Use authFetch so shared links work even when cookies are blocked
      // (Authorization: Bearer <token> is preferred by the backend).
      const response = await authFetch(`${config.API_URL}/api/share/${shareIdParam}`);
      if (!response.ok) {
        if (response.status === 401) {
          setCurrentView('auth');
          return;
        }
        throw new Error('Failed to load shared PDF');
      }
      
      const data = await response.json();
      const baseUrl = config.API_URL.endsWith('/') ? config.API_URL.slice(0, -1) : config.API_URL;
      const pdfPath = data.pdfUrl.startsWith('/') ? data.pdfUrl : `/${data.pdfUrl}`;
      const fullUrl = `${baseUrl}${pdfPath}`;
      
      setPdfUrl(fullUrl);
      setPdfId(data.pdfId);
      setShareId(data.shareId);
      setIsSharedPdfView(true);
      const assigned = data.assignedReviews || [];
      setPdfAssignedReviews(assigned);

      // Choose active review context:
      // - If a specific reviewId was provided in the share link and matches one
      //   of the assigned reviews, use that.
      // - Otherwise, fall back to the first assigned review (if any).
      let activeReview = null;
      if (assigned.length > 0) {
        if (reviewContextIdParam) {
          const ctxId = String(reviewContextIdParam);
          activeReview = assigned.find((r) => String(r.id) === ctxId) || assigned[0];
        } else {
          activeReview = assigned[0];
        }
      }

      console.log('[App] loadSharedPDF assigned reviews:', {
        assigned,
        activeReview,
      });

      if (activeReview) {
        setSelectedReview(activeReview);
        setPdfCurrentReview(activeReview);
      } else {
        setSelectedReview(null);
        setPdfCurrentReview(null);
      }
      setEditorBackTarget('reviews');
      setCurrentView('editor');
      setPdfReady(false);
    } catch (error) {
      console.error('Error loading shared PDF:', error);
      alert('Failed to load shared PDF. You may be logged in, but the shared document could not be opened. Showing your upload screen instead.');
      // If this fails after a successful login, don't leave the user stuck on the auth screen.
      setCurrentView('upload');
      resetEditorState();
    }
  }, []);

  const loadSharedReview = useCallback(async (shareIdParam) => {
    try {
      const response = await authFetch(`${config.API_URL}/api/share/review/${shareIdParam}`);
      if (!response.ok) {
        if (response.status === 401) {
          setCurrentView('auth');
          return;
        }
        throw new Error('Failed to load shared review');
      }
      const data = await response.json();
      setSelectedReview(data.review);
      setIsSharedReview(true);
      setIsSharedPdfView(false);
      // When opening a shared review, always land on the PDFs list for that review
      // so the second user sees the full project context instead of a specific PDF.
      setCurrentView('pdfs');
    } catch (error) {
      console.error('Error loading shared review:', error);
      alert('Failed to load shared review. Please make sure you are logged in.');
    }
  }, []);

  // Check authentication on mount and handle share links
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shareParam = urlParams.get('share');
    const shareReviewParam = urlParams.get('shareReview');
    const shareReviewContextParam = urlParams.get('shareReviewId');

    const checkAuth = async () => {
      try {
        const response = await authFetch(`${config.API_URL}/api/auth/me`);
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setCurrentView('upload');
          if (shareParam) {
            loadSharedPDF(shareParam, shareReviewContextParam);
          } else if (shareReviewParam) {
            loadSharedReview(shareReviewParam);
          }
        } else {
          setCurrentView('auth');
        }
      } catch (err) {
        setCurrentView('auth');
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, [loadSharedPDF, loadSharedReview]);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    const params = new URLSearchParams(window.location.search);
    const shareParam = params.get('share');
    const shareReviewParam = params.get('shareReview');
    const shareReviewContextParam = params.get('shareReviewId');
    if (shareParam) {
      loadSharedPDF(shareParam, shareReviewContextParam);
    } else if (shareReviewParam) {
      loadSharedReview(shareReviewParam);
    } else {
      setCurrentView('upload');
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${config.API_URL}/api/auth/logout`, {
        method: 'POST',
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    clearAuthToken();
    setUser(null);
    setCurrentView('auth');
    setSelectedReview(null);
    resetEditorState();
  };

  const handleSelectReview = (review) => {
    setSelectedReview(review);
    setIsSharedReview(false);
    setCurrentView('pdfs');
  };

  const handleBackToReviews = () => {
    setSelectedReview(null);
    setIsSharedReview(false);
    setCurrentView('reviews');
  };

  const handleSelectPDF = async (pdf) => {
    try {
      const contextReviewId = pdf.reviewId || selectedReview?.id || null;
      console.log('[App] handleSelectPDF context:', {
        pdfId: pdf.id,
        shareId: pdf.shareId,
        contextReviewId,
      });
      // Load PDF details
      const response = await authFetch(`${config.API_URL}/api/pdf/${pdf.id}`);
      if (!response.ok) throw new Error('Failed to load PDF');
      
      const data = await response.json();
      const baseUrl = config.API_URL.endsWith('/') ? config.API_URL.slice(0, -1) : config.API_URL;
      const pdfPath = data.pdfUrl.startsWith('/') ? data.pdfUrl : `/${data.pdfUrl}`;
      const fullUrl = `${baseUrl}${pdfPath}`;
      
      setPdfUrl(fullUrl);
      setPdfId(pdf.id);
      setShareId(pdf.shareId);
      setIsSharedPdfView(false);
      const assigned = data.assignedReviews || (data.currentReview ? [data.currentReview] : []);

      let activeReview = null;
      if (assigned.length > 0) {
        if (contextReviewId) {
          const ctxId = String(contextReviewId);
          activeReview = assigned.find((r) => String(r.id) === ctxId) || assigned[0];
        } else {
          activeReview = assigned[0];
        }
      }

      console.log('[App] handleSelectPDF assigned reviews:', {
        assigned,
        activeReview,
      });

      setPdfCurrentReview(activeReview || null);
      setPdfAssignedReviews(assigned);
      if (activeReview) {
        setSelectedReview(activeReview);
        setIsSharedReview(false);
      }
      setEditorBackTarget('pdfs');
      setCurrentView('editor');
      setPdfReady(false);
    } catch (err) {
      console.error('Error loading PDF:', err);
      alert('Failed to load PDF');
    }
  };

  const handleBackFromEditor = () => {
    if (editorBackTarget === 'pdfs') {
      setCurrentView('pdfs');
    } else if (editorBackTarget === 'upload') {
      setCurrentView('upload');
    } else {
      setCurrentView('reviews');
    }
    resetEditorState();
    setIsSharedPdfView(false);
  };

  if (checkingAuth) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="transition-loading-card">
            <div className="transition-loading-spinner" />
            <div className="transition-loading-text">Checking your session…</div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'auth') {
    return (
      <div className="App">
        <Auth onAuthSuccess={handleAuthSuccess} />
      </div>
    );
  }

  if (currentView === 'upload') {
    return (
      <div className="App">
        <UploadLanding
          user={user}
          onGoToReviews={() => setCurrentView('reviews')}
          onLogout={handleLogout}
          onOpenMasterPDF={async (pdf) => {
            try {
              const response = await authFetch(`${config.API_URL}/api/pdf/${pdf.id}`);
              if (!response.ok) throw new Error('Failed to load PDF');
              const data = await response.json();
              const baseUrl = config.API_URL.endsWith('/') ? config.API_URL.slice(0, -1) : config.API_URL;
              const pdfPath = data.pdfUrl.startsWith('/') ? data.pdfUrl : `/${data.pdfUrl}`;
              const fullUrl = `${baseUrl}${pdfPath}`;

              // Open the master copy from the upload page: no active review filter,
              // so all comments from all reviews are visible in this editor.
              setSelectedReview(null);
              setIsSharedReview(false);
              setPdfUrl(fullUrl);
              setPdfId(pdf.id);
              setShareId(pdf.shareId);
              setIsSharedPdfView(false);
              setPdfCurrentReview(null);
              setPdfAssignedReviews(data.assignedReviews || (data.currentReview ? [data.currentReview] : []));
              setEditorBackTarget('upload');
              setCurrentView('editor');
              setPdfReady(false);
            } catch (err) {
              console.error('Error loading master PDF:', err);
              alert('Failed to load PDF');
            }
          }}
          onUploaded={({ url, pdfId: newPdfId, shareId: newShareId }) => {
            setSelectedReview(null);
            setIsSharedReview(false);
            setPdfUrl(url);
            setPdfId(newPdfId);
            setShareId(newShareId);
            // Treat freshly uploaded PDFs as \"not yet assigned\" in the UI,
            // even though they are stored under the hidden Inbox review.
            setPdfCurrentReview(null);
            setEditorBackTarget('upload');
            setCurrentView('editor');
            setPdfReady(false);
          }}
        />
      </div>
    );
  }

  if (currentView === 'reviews') {
    return (
      <div className="App">
        <ReviewsList
          user={user}
          onBackToUpload={() => setCurrentView('upload')}
          onSelectReview={handleSelectReview}
          onCreateReview={() => {}} // Handled internally
          onLogout={handleLogout}
        />
      </div>
    );
  }

  if (currentView === 'pdfs') {
    return (
      <div className="App">
        <PDFsList
          review={selectedReview}
          user={user}
          onSelectPDF={handleSelectPDF}
          onBackToReviews={handleBackToReviews}
          isSharedReview={isSharedReview}
        />
      </div>
    );
  }

  // Editor view
  return (
    <div className="App">
      {/* Navigation bar */}
      <div className="editor-nav">
        <button onClick={handleBackFromEditor} className="nav-back-button">
          ← {editorBackTarget === 'pdfs' ? 'Back to PDFs' : editorBackTarget === 'upload' ? 'Back to Upload' : 'Back to Reviews'}
        </button>
        <div className="editor-nav-actions">
          {user && pdfId && !isSharedPdfView && (
            <EditorAssignMenu
              pdfId={pdfId}
              currentReview={pdfCurrentReview}
              assignedReviews={pdfAssignedReviews}
              onAssignedToReview={(review) => {
                // Always track all reviews this PDF belongs to
                setPdfAssignedReviews((prev) => {
                  const exists = prev.some((r) => r.id === review.id);
                  return exists ? prev : [...prev, review];
                });
                // If this PDF wasn't yet assigned to any real review (e.g. came from Inbox/upload),
                // treat this as the primary assignment and update navigation context so that
                // the back button returns to that review's PDFs. For subsequent assignments,
                // keep the current review context instead of jumping to the newly assigned one.
                if (!pdfCurrentReview) {
                  setPdfCurrentReview(review);
                  setSelectedReview(review);
                  setIsSharedReview(false);
                  setEditorBackTarget('pdfs');
                }
              }}
            />
          )}
          {shareId && (
            <SharePanel
              shareId={shareId}
              reviewId={pdfCurrentReview?.id || selectedReview?.id || null}
            />
          )}
        </div>
      </div>
      
      <div className="editor-container">
        <div className={`pdf-section ${pdfReady && !commentPanelVisible ? 'pdf-section-full' : ''} ${!pdfReady ? 'pdf-section-loading' : ''}`}>
          <PDFViewer 
            pdfUrl={pdfUrl} 
            pdfId={pdfId}
            shareId={shareId}
            userName={userName}
            activeReviewId={selectedReview?.id || null}
            comments={comments}
            onCommentsChange={setComments}
            onCommentDelete={commentDeleteRef}
            onCommentUpdate={commentUpdateRef}
            onCommentReply={commentReplyRef}
            onCommentMarkRead={commentMarkReadRef}
            exportRef={exportRef}
            sharePanel={null} // Handled in nav bar
            selectedCommentId={selectedCommentId}
            onSelectComment={(comment) => {
              setSelectedCommentId(comment?.id ?? null);
              if (comment?.page && exportRef.current?.setPageNumber) {
                exportRef.current.setPageNumber(comment.page);
              }
            }}
            onDocumentReady={() => setPdfReady(true)}
          />
        </div>
        {pdfReady && (
          <>
            <button
              type="button"
              className={`comments-toggle-icon ${commentPanelVisible ? '' : 'comments-toggle-icon--show-only'}`}
              onClick={() => setCommentPanelVisible(!commentPanelVisible)}
              title={commentPanelVisible ? 'Hide comments' : 'Show comments'}
              aria-label={commentPanelVisible ? 'Hide comments panel' : 'Show comments panel'}
            >
              {commentPanelVisible ? '▶' : '◀'}
            </button>
            <div className={`comments-section ${commentPanelVisible ? '' : 'comments-section--hidden'}`}>
              <CommentPanel 
                pdfId={pdfId}
                comments={comments}
                onCommentsChange={setComments}
                onDeleteComment={(id) => commentDeleteRef.current?.(id)}
                onUpdateComment={(id, text) => commentUpdateRef.current?.(id, text)}
                onReplyComment={(commentId, reply) => commentReplyRef.current?.(commentId, reply)}
                onMarkCommentRead={(id, isRead) => commentMarkReadRef.current?.(id, isRead)}
                userName={userName}
                selectedCommentId={selectedCommentId}
                onSelectComment={(comment) => {
                  setSelectedCommentId(comment?.id ?? null);
                  if (comment?.page && exportRef.current?.setPageNumber) {
                    exportRef.current.setPageNumber(comment.page);
                  }
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EditorAssignMenu({ pdfId, currentReview, assignedReviews = [], onAssignedToReview }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const loadReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${config.API_URL}/api/reviews`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load reviews');
      }
      const data = await res.json();
      setReviews(data.reviews || []);
    } catch (e) {
      setError(e.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const openModal = async () => {
    setOpen(true);
    await loadReviews();
  };

  const assignToReview = async (reviewId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${config.API_URL}/api/pdf/${pdfId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to assign PDF');
      setOpen(false);
      onAssignedToReview(data.review);
    } catch (e) {
      setError(e.message || 'Failed to assign PDF');
    } finally {
      setLoading(false);
    }
  };

  const createAndAssign = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${config.API_URL}/api/pdf/${pdfId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle.trim(), description: newDescription.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create review');
      setOpen(false);
      onAssignedToReview(data.review);
    } catch (e2) {
      setError(e2.message || 'Failed to create review');
    } finally {
      setLoading(false);
    }
  };

  const hasAssignedReviews = Array.isArray(assignedReviews) && assignedReviews.length > 0;

  return (
    <>
      {hasAssignedReviews && (
        <span
          className="editor-assigned-tag"
          title={`Assigned to: ${assignedReviews.map((r) => r.title).join(', ')}`}
        >
          <span className="editor-assigned-dot" />
          <span className="editor-assigned-label">Assigned to</span>
          <span className="editor-assigned-title">
            {assignedReviews.map((r) => r.title).join(', ')}
          </span>
        </span>
      )}
      <button type="button" className="editor-assign-btn" onClick={openModal} title="Assign this PDF to a review">
        Assign to review
      </button>

      {open && (
        <div className="assign-modal-backdrop" role="dialog" aria-modal="true">
          <div className="assign-modal">
            <div className="assign-modal-header">
              <div>
                <h3>Assign PDF to a review</h3>
                <p>Select an existing review, or create a new one.</p>
              </div>
              <button
                type="button"
                className="assign-modal-close"
                onClick={() => setOpen(false)}
                disabled={loading}
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </div>

            {error && <div className="assign-modal-error">{error}</div>}

            <div className="assign-modal-body">
              <div className="assign-modal-section">
                {hasAssignedReviews && (
                  <div className="assign-modal-current">
                    <span className="assign-modal-current-label">Assigned to:</span>
                    <span className="assign-modal-current-title">
                      {assignedReviews.map((r) => r.title).join(', ')}
                    </span>
                  </div>
                )}
                <div className="assign-modal-section-title">Assign to existing</div>
                {loading && reviews.length === 0 ? (
                  <div className="assign-modal-loading">Loading reviews…</div>
                ) : reviews.length === 0 ? (
                  <div className="assign-modal-empty">No reviews yet.</div>
                ) : (
                  <div className="assign-modal-list">
                    {reviews.map((r) => {
                      const isCurrent = currentReview && r.id === currentReview.id;
                      const isAssigned = hasAssignedReviews && assignedReviews.some((ar) => ar.id === r.id);
                      return (
                        <button
                          key={r.id}
                          type="button"
                          className={`assign-modal-item${
                            isCurrent ? ' assign-modal-item--current' : ''
                          }${isAssigned ? ' assign-modal-item--assigned' : ''}`}
                          onClick={() => assignToReview(r.id)}
                          disabled={loading}
                        >
                          <div className="assign-modal-item-header">
                            <div className="assign-modal-item-title">{r.title}</div>
                              {isAssigned && (
                                <span className="assign-modal-item-badge">
                                  {isCurrent ? 'Primary assignment' : 'Assigned'}
                                </span>
                              )}
                          </div>
                          {r.description ? <div className="assign-modal-item-desc">{r.description}</div> : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="assign-modal-divider" />

              <div className="assign-modal-section">
                <div className="assign-modal-section-title">Create new review</div>
                <form onSubmit={createAndAssign} className="assign-modal-form">
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Review title (required)"
                    disabled={loading}
                    className="assign-modal-input"
                    required
                  />
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Description (optional)"
                    disabled={loading}
                    className="assign-modal-textarea"
                    rows={3}
                  />
                  <button type="submit" className="assign-modal-submit" disabled={loading || !newTitle.trim()}>
                    {loading ? 'Working…' : 'Create & assign'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
